import { createSessionId, type Citation } from "@gtd/shared";
import { renderWebsiteReply, websiteLinks } from "./messageContent";
import { TranscriptViewport } from "./transcriptViewport";
import styleText from "./styles.css?raw";

type Message = {
  role: "user" | "assistant";
  content: string;
  citations?: Citation[];
  createdAt?: Date;
};

type ActionSuggestionType = "book" | "call" | "message" | "none";
type ActionSuggestion = {
  type: ActionSuggestionType;
  customerName?: string;
  contact?: string;
  email?: string;
  desiredAt?: string;
  notes?: string;
  reason?: string;
};

type DayOfWeek = "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday";

type TimeRange = {
  start: string;
  end: string;
};

type BusinessHours = {
  timeZone?: string;
  weekly: { day: DayOfWeek; ranges: TimeRange[] }[];
};

type BusyWindow = {
  start: string;
  end: string;
  source?: string;
};

type AvailabilityState = {
  loading: boolean;
  busy: BusyWindow[];
  businessHours?: BusinessHours;
  timeZone?: string;
  windowStart?: string;
  windowEnd?: string;
  fetchedAt?: string;
  error?: string;
};

type CalendarResult = {
  status: "scheduled" | "skipped" | "failed";
  provider?: string;
  reason?: string;
  eventId?: string;
  start?: string;
  end?: string;
  error?: string;
  busy?: BusyWindow[];
};

type SlotStatus = "free" | "busy" | "closed";

const DEFAULT_APPOINTMENT_MINUTES = 30;

const API_BASE =
  (window as any).__GTD_WIDGET_API__ ||
  import.meta.env.VITE_API_BASE_URL ||
  "http://localhost:3001";

const scriptEl = document.currentScript as HTMLScriptElement | null;
const siteKey = scriptEl?.dataset.siteKey;
const themeName = (scriptEl?.dataset.theme || "ocean").toLowerCase();
const themes: Record<string, Record<string, string>> = {
  ocean: {
    "--gtd-accent-1": "#2563eb",
    "--gtd-accent-2": "#22d3ee",
    "--gtd-bg": "#f8fafc",
    "--gtd-surface": "#ffffff",
    "--gtd-text": "#0f172a",
    "--gtd-muted": "#475569",
    "--gtd-border": "#e2e8f0",
    "--gtd-bubble-user": "#2563eb",
    "--gtd-bubble-assistant": "#ffffff",
    "--gtd-button-text": "#ffffff"
  },
  daydream: {
    "--gtd-accent-1": "#10a37f",
    "--gtd-accent-2": "#0f8c6b",
    "--gtd-bg": "#f7f7f8",
    "--gtd-surface": "#ffffff",
    "--gtd-text": "#0f172a",
    "--gtd-muted": "#4b5563",
    "--gtd-border": "#e5e7eb",
    "--gtd-bubble-user": "#e5e7eb",
    "--gtd-bubble-assistant": "#ffffff",
    "--gtd-button-text": "#ffffff"
  },
  sunrise: {
    "--gtd-accent-1": "#f97316",
    "--gtd-accent-2": "#facc15",
    "--gtd-bg": "#fff7ed",
    "--gtd-surface": "#ffffff",
    "--gtd-text": "#1f2937",
    "--gtd-muted": "#6b7280",
    "--gtd-border": "#f4c38c",
    "--gtd-bubble-user": "#f97316",
    "--gtd-bubble-assistant": "#fff7ed",
    "--gtd-button-text": "#1f2937"
  },
  slate: {
    "--gtd-accent-1": "#0ea5e9",
    "--gtd-accent-2": "#0f172a",
    "--gtd-bg": "#0b1220",
    "--gtd-surface": "#0f172a",
    "--gtd-text": "#e2e8f0",
    "--gtd-muted": "#94a3b8",
    "--gtd-border": "#1e293b",
    "--gtd-bubble-user": "#0ea5e9",
    "--gtd-bubble-assistant": "#111827",
    "--gtd-button-text": "#ffffff"
  },
  evergreen: {
    "--gtd-accent-1": "#16a34a",
    "--gtd-accent-2": "#0f766e",
    "--gtd-bg": "#08130e",
    "--gtd-surface": "#0e1f16",
    "--gtd-text": "#e4f5eb",
    "--gtd-muted": "#92b8a3",
    "--gtd-border": "#123425",
    "--gtd-bubble-user": "#16a34a",
    "--gtd-bubble-assistant": "#0f2419",
    "--gtd-button-text": "#e4f5eb"
  },
  nightdream: {
    "--gtd-accent-1": "#10a37f",
    "--gtd-accent-2": "#0c7c65",
    "--gtd-bg": "#05070a",
    "--gtd-surface": "#0c1117",
    "--gtd-text": "#e5e7eb",
    "--gtd-muted": "#94a3b8",
    "--gtd-border": "#1f2933",
    "--gtd-bubble-user": "#111827",
    "--gtd-bubble-assistant": "#0c1117",
    "--gtd-button-text": "#e5f4ed"
  }
};
const themeVars: Record<string, string> = themes[themeName] ?? themes.ocean!;

if (!siteKey) {
  console.error("[gtd-widget] data-site-key missing on script tag");
} else {
  bootstrap(siteKey);
}

function bootstrap(siteKey: string) {
  const sessionId = ensureSession(siteKey);
  const host = document.createElement("div");
  host.className = "gtd-root";
  const shadow = host.attachShadow({ mode: "open" });
  attachStyles(shadow, themeVars);
  document.body.appendChild(host);
  const transcript = new TranscriptViewport();
  let focusComposerAfterReply = false;
  const cancelPendingFocus = (event: Event) => {
    if (!focusComposerAfterReply) return;
    const target = event.composedPath()[0];
    if (target instanceof HTMLElement && !target.matches(".gtd-input, .gtd-send, body")) {
      focusComposerAfterReply = false;
    }
  };
  document.addEventListener("pointerdown", cancelPendingFocus, true);
  document.addEventListener("focusin", cancelPendingFocus, true);

  const showQuickAction = false; // toggle on if you want the booking button visible by default

  const state: {
    open: boolean;
    loading: boolean;
    draft: string;
    error?: string;
    messages: Message[];
    contactOpen: boolean;
    actionLoading: boolean;
    actionStatus?: { type: "success" | "error"; text: string };
    contact: { name: string; email: string; notes: string };
    contactMode: "message" | "call";
    booking: {
      open: boolean;
      name: string;
      email: string;
      desiredAt: string;
      notes: string;
      loading: boolean;
      status?: { type: "success" | "error"; text: string };
      availability: AvailabilityState;
      availabilitySheetOpen: boolean;
      selectedAvailabilityDate?: string;
    };
    suggestion?: ActionSuggestion;
  } = {
    open: false,
    loading: false,
    draft: "",
    messages: [{ role: "assistant", content: buildGreeting(), createdAt: new Date() }],
    contactOpen: false,
    actionLoading: false,
    contact: { name: "", email: "", notes: "" },
    contactMode: "message",
    booking: {
      open: false,
      name: "",
      email: "",
      desiredAt: "",
      notes: "",
      loading: false,
      availability: { loading: false, busy: [] },
      availabilitySheetOpen: false
    }
  };

  function openBooking(openSheet = false) {
    state.booking.open = true;
    state.booking.status = undefined;
    render();
    const lastFetch = state.booking.availability.fetchedAt
      ? new Date(state.booking.availability.fetchedAt).getTime()
      : 0;
    if (
      !state.booking.availability.loading &&
      (!lastFetch || Date.now() - lastFetch > 5 * 60 * 1000)
    ) {
      void loadAvailability();
    }
    if (openSheet) {
      state.booking.availabilitySheetOpen = true;
    }
  }

  function openAvailabilitySheet() {
    state.booking.availabilitySheetOpen = true;
    if (!state.booking.selectedAvailabilityDate) {
      const start = state.booking.availability.windowStart
        ? new Date(state.booking.availability.windowStart)
        : new Date();
      state.booking.selectedAvailabilityDate = start.toDateString();
    }
    render();
    if (!state.booking.availability.loading && !state.booking.availability.fetchedAt) {
      void loadAvailability();
    }
  }

  async function loadAvailability() {
    state.booking.availability.loading = true;
    state.booking.availability.error = undefined;
    render();

    const start = new Date();
    const end = new Date(start.getTime() + 14 * 24 * 60 * 60 * 1000);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    try {
      const resp = await fetch(`${API_BASE}/v1/integrations/google/availability`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          siteKey,
          start: start.toISOString(),
          end: end.toISOString(),
          timeZone
        })
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || `Availability error ${resp.status}`);
      }
      const data = (await resp.json()) as any;
      state.booking.availability = {
        loading: false,
        busy: Array.isArray(data?.busy) ? data.busy : [],
        businessHours: data?.businessHours,
        timeZone: data?.timeZone,
        windowStart: data?.windowStart,
        windowEnd: data?.windowEnd,
        fetchedAt: new Date().toISOString()
      };
      if (data?.windowStart) {
        state.booking.selectedAvailabilityDate = new Date(data.windowStart).toDateString();
      }
    } catch (err) {
      console.error("[gtd-widget] availability error", err);
      state.booking.availability.loading = false;
      state.booking.availability.error =
        err instanceof Error ? err.message : "Unable to read calendar availability.";
    } finally {
      render();
    }
  }

  render();

  function render() {
    const previousInput = shadow.querySelector<HTMLTextAreaElement>(".gtd-input");
    const composerFocused = previousInput === shadow.activeElement;
    const selection: [number, number] | null = previousInput
      ? [previousInput.selectionStart, previousInput.selectionEnd]
      : null;
    transcript.detach();
    shadow.innerHTML = "";
    attachStyles(shadow, themeVars);

    if (!state.open) {
      focusComposerAfterReply = false;
      const button = document.createElement("button");
      button.className = "gtd-button";
      button.setAttribute("aria-label", "Open website assistant");
      button.innerHTML = `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
        </svg>
      `;
      button.onclick = () => {
        state.open = true;
        render();
      };
      shadow.appendChild(button);
      return;
    }

    // Backdrop
    const backdrop = document.createElement("div");
    backdrop.className = "gtd-backdrop";
    backdrop.onclick = () => {
      state.open = false;
      render();
    };
    shadow.appendChild(backdrop);

    // Panel container
    const panelContainer = document.createElement("div");
    panelContainer.className = "gtd-panel-container";
    panelContainer.onclick = (e) => {
      if (e.target === panelContainer) {
        state.open = false;
        render();
      }
    };

    const panel = document.createElement("div");
    panel.className = "gtd-panel";

    const header = document.createElement("div");
    header.className = "gtd-header";

    const headerContent = document.createElement("div");
    headerContent.className = "gtd-header-content";

    const avatar = document.createElement("div");
    avatar.className = "gtd-avatar";
    avatar.innerHTML = `
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
              d="M13 10V3L4 14h7v7l9-11h-7z"/>
      </svg>
    `;

    const statusIndicator = document.createElement("div");
    statusIndicator.className = "gtd-status-indicator";
    avatar.appendChild(statusIndicator);

    const headerText = document.createElement("div");
    headerText.className = "gtd-header-text";
    const title = document.createElement("h3");
    title.textContent = "Menerai AI";
    const headerStatus = document.createElement("div");
    headerStatus.className = "gtd-header-status";
    headerStatus.innerHTML = `
      <div class="gtd-status-dot"></div>
      <span>Always online · Instant responses</span>
    `;
    headerText.appendChild(title);
    headerText.appendChild(headerStatus);

    headerContent.appendChild(avatar);
    headerContent.appendChild(headerText);

    const closeBtn = document.createElement("button");
    closeBtn.className = "gtd-close";
    closeBtn.setAttribute("aria-label", "Close website assistant");
    closeBtn.innerHTML = `
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
      </svg>
    `;
    closeBtn.onclick = () => {
      state.open = false;
      render();
    };

    header.appendChild(headerContent);
    header.appendChild(closeBtn);
    panel.appendChild(header);

    const messagesEl = document.createElement("div");
    messagesEl.className = "gtd-messages";
    messagesEl.setAttribute("role", "log");
    messagesEl.setAttribute("aria-label", "Conversation");
    messagesEl.tabIndex = 0;
    state.messages.forEach((msg) => {
      const wrapper = document.createElement("div");
      wrapper.className = `gtd-message-wrapper ${msg.role}`;

      const bubble = document.createElement("div");
      bubble.className = `gtd-message ${msg.role}`;
      const links =
        msg.role === "assistant" ? websiteLinks(msg.citations ?? [], location.href) : [];
      const { content, related } = renderWebsiteReply(msg.content, links);
      bubble.appendChild(content);
      if (related) bubble.appendChild(related);

      const timestamp = document.createElement("div");
      timestamp.className = "gtd-message-time";
      const now = msg.createdAt ?? new Date();
      timestamp.textContent = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      bubble.appendChild(timestamp);

      wrapper.appendChild(bubble);
      messagesEl.appendChild(wrapper);
    });
    panel.appendChild(messagesEl);

    // Quick replies for initial message
    if (state.messages.length === 1) {
      const quickReplies = document.createElement("div");
      quickReplies.className = "gtd-quick-replies";
      const replies = [
        "What services do you offer?",
        "Check availability",
        "Book a meeting",
        "Contact support"
      ];
      replies.forEach((reply) => {
        const button = document.createElement("button");
        button.className = "gtd-quick-reply";
        button.textContent = reply;
        button.onclick = () => {
          const input = shadow.querySelector(".gtd-input") as HTMLTextAreaElement;
          if (input) {
            input.value = reply;
            const sendBtn = shadow.querySelector(".gtd-send") as HTMLButtonElement;
            if (sendBtn) sendBtn.click();
          }
        };
        quickReplies.appendChild(button);
      });
      panel.appendChild(quickReplies);
    }

    if (state.suggestion) {
      const sugg = document.createElement("div");
      sugg.className = "gtd-suggestion";
      const text = document.createElement("div");
      text.className = "gtd-suggestion-text";
      text.textContent =
        state.suggestion.type === "book"
          ? "Ready to book this for you?"
          : state.suggestion.type === "call"
            ? "Want us to set up a call?"
            : "Should we have the team follow up?";
      const reason = document.createElement("div");
      reason.className = "gtd-suggestion-reason";
      if (state.suggestion.reason) {
        reason.textContent = state.suggestion.reason;
      }
      const actions = document.createElement("div");
      actions.className = "gtd-suggestion-actions";
      const confirm = document.createElement("button");
      confirm.className = "gtd-suggestion-confirm";
      confirm.textContent =
        state.suggestion.type === "book"
          ? "Confirm booking"
          : state.suggestion.type === "call"
            ? "Request call"
            : "Yes, follow up";
      confirm.onclick = () => applySuggestion(state.suggestion!);
      if (state.suggestion.type === "book") {
        const pickTime = document.createElement("button");
        pickTime.className = "gtd-suggestion-confirm";
        pickTime.textContent = "Pick time";
        pickTime.onclick = () => {
          applySuggestion(state.suggestion!);
          openAvailabilitySheet();
        };
        actions.appendChild(pickTime);
      }
      const dismiss = document.createElement("button");
      dismiss.className = "gtd-suggestion-dismiss";
      dismiss.textContent = "Dismiss";
      dismiss.onclick = () => {
        state.suggestion = undefined;
        render();
      };
      actions.appendChild(confirm);
      actions.appendChild(dismiss);
      sugg.appendChild(text);
      sugg.appendChild(reason);
      sugg.appendChild(actions);
      panel.appendChild(sugg);
    }

    const statusEl = document.createElement("div");
    statusEl.className = "gtd-status";
    statusEl.textContent = state.error
      ? state.error
      : state.loading
        ? "Thinking..."
        : "Ask anything about this business.";
    panel.appendChild(statusEl);

    if (showQuickAction) {
      const quickActions = document.createElement("div");
      quickActions.className = "gtd-quick-actions";
      const bookBtn = document.createElement("button");
      bookBtn.className = "gtd-quick-action";
      bookBtn.textContent = "Book a time";
      bookBtn.onclick = () => {
        openBooking();
      };
      quickActions.appendChild(bookBtn);
      panel.appendChild(quickActions);
    }

    if (state.booking.open) {
      const bookCard = document.createElement("div");
      bookCard.className = "gtd-book-card";

      const bookHeader = document.createElement("div");
      bookHeader.className = "gtd-book-header";
      const bookTitle = document.createElement("div");
      bookTitle.className = "gtd-book-title";
      bookTitle.textContent = "Schedule a time";
      const bookClose = document.createElement("button");
      bookClose.className = "gtd-book-close";
      bookClose.textContent = "×";
      bookClose.onclick = () => {
        state.booking.open = false;
        render();
      };
      bookHeader.appendChild(bookTitle);
      bookHeader.appendChild(bookClose);
      bookCard.appendChild(bookHeader);

      const bookForm = document.createElement("div");
      bookForm.className = "gtd-book-form";
      bookForm.appendChild(
        buildField("Name", "Your name", state.booking.name, (val) => {
          state.booking.name = val;
        })
      );
      bookForm.appendChild(
        buildField("Email", "you@example.com", state.booking.email, (val) => {
          state.booking.email = val;
        })
      );

      const slotIssue = getSlotIssue();

      const timeWrapper = document.createElement("label");
      timeWrapper.className = "gtd-field";
      const timeLabel = document.createElement("span");
      timeLabel.textContent = "Preferred time";
      const timeInput = document.createElement("input");
      timeInput.type = "datetime-local";
      timeInput.value = state.booking.desiredAt;
      timeInput.oninput = (e) => {
        state.booking.desiredAt = (e.target as HTMLInputElement).value;
        state.booking.status = undefined;
        render();
      };
      timeWrapper.appendChild(timeLabel);
      timeWrapper.appendChild(timeInput);
      bookForm.appendChild(timeWrapper);

      const availTrigger = document.createElement("button");
      availTrigger.className = "gtd-availability-trigger";
      availTrigger.type = "button";
      availTrigger.textContent = state.booking.availability.loading
        ? "Loading availability..."
        : "Check availability";
      availTrigger.disabled = state.booking.availability.loading;
      availTrigger.onclick = () => {
        openAvailabilitySheet();
      };
      bookForm.appendChild(availTrigger);

      bookForm.appendChild(
        buildField(
          "Notes",
          "Any context for the meeting",
          state.booking.notes,
          (val) => {
            state.booking.notes = val;
          },
          true
        )
      );

      const bookStatus = document.createElement("div");
      bookStatus.className = "gtd-book-status";
      if (state.booking.status) {
        bookStatus.textContent = state.booking.status.text;
        bookStatus.dataset.state = state.booking.status.type;
      } else if (slotIssue && slotIssue.type !== "missing") {
        bookStatus.textContent = slotIssue.message;
        bookStatus.dataset.state = "error";
      } else if (
        state.booking.desiredAt &&
        !state.booking.availability.loading &&
        !state.booking.availability.error &&
        state.booking.availability.fetchedAt
      ) {
        bookStatus.textContent = "This time looks free.";
        bookStatus.dataset.state = "success";
      }
      bookForm.appendChild(bookStatus);

      const bookSubmit = document.createElement("button");
      bookSubmit.className = "gtd-book-submit";
      bookSubmit.textContent = state.booking.loading ? "Booking..." : "Confirm booking";
      const hasBlockingIssue =
        slotIssue &&
        (slotIssue.type === "busy" || slotIssue.type === "invalid" || slotIssue.type === "closed");
      bookSubmit.disabled = state.booking.loading || !!hasBlockingIssue;
      bookSubmit.onclick = async () => {
        await sendBooking();
      };
      bookForm.appendChild(bookSubmit);

      bookCard.appendChild(bookForm);
      panel.appendChild(bookCard);
    }

    const contactCard = document.createElement("div");
    contactCard.className = "gtd-contact-card";
    const contactHeader = document.createElement("div");
    contactHeader.className = "gtd-contact-header";
    const contactTitle = document.createElement("div");
    contactTitle.className = "gtd-contact-title";
    contactTitle.textContent =
      state.contactMode === "call" ? "Want a call back?" : "Want the team to follow up?";
    const contactToggle = document.createElement("button");
    contactToggle.className = "gtd-contact-toggle";
    contactToggle.textContent = state.contactOpen ? "Hide" : "Share details";
    contactToggle.onclick = () => {
      state.contactOpen = !state.contactOpen;
      render();
    };
    contactHeader.appendChild(contactTitle);
    contactHeader.appendChild(contactToggle);
    contactCard.appendChild(contactHeader);

    if (state.contactOpen) {
      const form = document.createElement("div");
      form.className = "gtd-contact-form";

      const nameField = buildField("Name", "Your name", state.contact.name, (val) => {
        state.contact.name = val;
      });
      const emailField = buildField("Email", "you@example.com", state.contact.email, (val) => {
        state.contact.email = val;
      });
      const notesField = buildField(
        "What do you need?",
        "Share any context",
        state.contact.notes,
        (val) => {
          state.contact.notes = val;
        },
        true
      );

      form.appendChild(nameField);
      form.appendChild(emailField);
      form.appendChild(notesField);

      const actionStatus = document.createElement("div");
      actionStatus.className = "gtd-contact-status";
      if (state.actionStatus) {
        actionStatus.textContent = state.actionStatus.text;
        actionStatus.dataset.state = state.actionStatus.type;
      }
      form.appendChild(actionStatus);

      const submit = document.createElement("button");
      submit.className = "gtd-contact-submit";
      submit.textContent =
        state.actionLoading && state.contactMode === "call"
          ? "Requesting..."
          : state.actionLoading
            ? "Sending..."
            : state.contactMode === "call"
              ? "Request a call"
              : "Send to team";
      submit.disabled = state.actionLoading;
      submit.onclick = async () => {
        await sendAction();
      };
      form.appendChild(submit);
      contactCard.appendChild(form);
    }

    panel.appendChild(contactCard);

    const footer = document.createElement("div");
    footer.className = "gtd-footer";

    const inputWrapper = document.createElement("div");
    inputWrapper.className = "gtd-input-wrapper";

    const input = document.createElement("textarea");
    input.className = "gtd-input";
    input.placeholder = "Type your message...";
    input.setAttribute("aria-label", "Your message");
    input.maxLength = 8000;
    input.value = state.draft;
    input.disabled = state.loading;
    input.rows = 1;
    input.oninput = () => {
      state.draft = input.value;
      input.style.height = "auto";
      input.style.height = `${Math.min(input.scrollHeight, 120)}px`;
      transcript.keepLatestVisible();
    };
    input.onfocus = () => transcript.keepLatestVisible();

    inputWrapper.appendChild(input);

    const sendBtn = document.createElement("button");
    sendBtn.className = "gtd-send";
    sendBtn.setAttribute("aria-label", "Send message");
    sendBtn.innerHTML = `
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
      </svg>
    `;
    sendBtn.disabled = state.loading;

    const send = async () => {
      const text = input.value.trim();
      if (!text || state.loading) return;
      focusComposerAfterReply = true;
      state.draft = "";
      input.value = "";
      input.style.height = "auto";
      state.error = undefined;
      state.messages.push({ role: "user", content: text, createdAt: new Date() });
      maybeOpenBooking(text);
      state.loading = true;
      render();
      try {
        const resp = await fetch(`${API_BASE}/v1/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            siteKey,
            sessionId,
            pageUrl: window.location.href,
            message: text
          })
        });
        if (!resp.ok) {
          throw new Error(`API error: ${resp.status}`);
        }
        const data = await resp.json();
        state.messages.push({
          role: "assistant",
          content: data.reply,
          citations: Array.isArray(data.citations) ? data.citations : [],
          createdAt: new Date()
        });
        if (data.actionSuggestion?.type && data.actionSuggestion.type !== "none") {
          state.suggestion = data.actionSuggestion as ActionSuggestion;
          if (state.suggestion.type === "book") {
            if (state.suggestion.customerName) state.booking.name = state.suggestion.customerName;
            if (state.suggestion.email) state.booking.email = state.suggestion.email;
            if (state.suggestion.desiredAt) state.booking.desiredAt = state.suggestion.desiredAt;
            if (state.suggestion.notes) state.booking.notes = state.suggestion.notes;
            openBooking(true);
          } else if (state.suggestion.type === "call") {
            state.contactOpen = true;
            state.contactMode = "call";
            if (state.suggestion.customerName) state.contact.name = state.suggestion.customerName;
            if (state.suggestion.email) state.contact.email = state.suggestion.email;
            if (state.suggestion.notes) state.contact.notes = state.suggestion.notes;
          }
        }
      } catch (err) {
        console.error("[gtd-widget] chat error", err);
        state.error = "We hit a snag. Please try again.";
      } finally {
        state.loading = false;
        render();
      }
    };

    sendBtn.onclick = send;
    input.onkeydown = (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        send();
      }
    };

    footer.appendChild(inputWrapper);
    footer.appendChild(sendBtn);
    panel.appendChild(footer);

    if (state.booking.availabilitySheetOpen) {
      panel.appendChild(renderAvailabilitySheet());
    }

    panelContainer.appendChild(panel);
    shadow.appendChild(panelContainer);
    transcript.attach(messagesEl, state.messages.length);
    if ((composerFocused || focusComposerAfterReply) && !state.loading) {
      input.focus({ preventScroll: true });
      if (composerFocused && selection) input.setSelectionRange(selection[0], selection[1]);
      focusComposerAfterReply = false;
    }
    if (state.draft) {
      input.style.height = "auto";
      input.style.height = `${Math.min(input.scrollHeight, 120)}px`;
      transcript.keepLatestVisible();
    }
  }

  type SlotIssue =
    | { type: "missing"; message: string }
    | { type: "invalid"; message: string }
    | { type: "busy"; message: string }
    | { type: "closed"; message: string };

  function buildAvailabilityBlock(slotIssue: SlotIssue | null) {
    const availability = state.booking.availability;
    const container = document.createElement("div");
    container.className = "gtd-availability";

    const head = document.createElement("div");
    head.className = "gtd-availability-head";
    const title = document.createElement("div");
    title.className = "gtd-availability-title";
    title.textContent = "Calendar availability";
    const refresh = document.createElement("button");
    refresh.className = "gtd-availability-refresh";
    refresh.textContent = availability.loading ? "Refreshing..." : "Refresh";
    refresh.disabled = availability.loading;
    refresh.onclick = () => loadAvailability();
    head.appendChild(title);
    head.appendChild(refresh);
    container.appendChild(head);

    const status = document.createElement("div");
    status.className = "gtd-availability-status";
    if (availability.loading) {
      status.textContent = "Checking busy times...";
    } else if (availability.error) {
      status.dataset.state = "error";
      status.textContent = `Cannot read calendar: ${availability.error}`;
    } else if (slotIssue && slotIssue.type === "busy") {
      status.dataset.state = "error";
      status.textContent = slotIssue.message;
    } else if (slotIssue && slotIssue.type === "closed") {
      status.dataset.state = "error";
      status.textContent = slotIssue.message;
    } else if (availability.busy.length === 0) {
      status.dataset.state = "success";
      status.textContent = "Looks clear for the next couple of weeks.";
    } else {
      status.textContent = "Upcoming busy times:";
    }
    container.appendChild(status);

    if (!availability.loading && !availability.error) {
      container.appendChild(buildBusyList(availability.busy, availability.timeZone));
      if (availability.businessHours) {
        container.appendChild(buildHoursList(availability.businessHours));
      }
    }

    return container;
  }

  function renderAvailabilitySheet() {
    const overlay = document.createElement("div");
    overlay.className = "gtd-availability-sheet";

    const sheet = document.createElement("div");
    sheet.className = "gtd-availability-panel";

    const header = document.createElement("div");
    header.className = "gtd-availability-panel-head";
    const title = document.createElement("div");
    title.className = "gtd-availability-panel-title";
    title.textContent = "Pick an available time";
    const close = document.createElement("button");
    close.className = "gtd-availability-close";
    close.textContent = "×";
    close.onclick = () => {
      state.booking.availabilitySheetOpen = false;
      render();
    };
    header.appendChild(title);
    header.appendChild(close);
    sheet.appendChild(header);

    const availability = state.booking.availability;
    const tz =
      availability.timeZone ||
      availability.businessHours?.timeZone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;

    const sub = document.createElement("div");
    sub.className = "gtd-availability-sub";
    sub.textContent = `Next 2 weeks${tz ? ` • ${tz}` : ""}`;
    sheet.appendChild(sub);

    const refresh = document.createElement("button");
    refresh.className = "gtd-availability-refresh";
    refresh.textContent = availability.loading ? "Refreshing..." : "Refresh";
    refresh.disabled = availability.loading;
    refresh.onclick = () => loadAvailability();
    sheet.appendChild(refresh);

    if (availability.loading) {
      const loading = document.createElement("div");
      loading.className = "gtd-muted";
      loading.textContent = "Checking busy times...";
      sheet.appendChild(loading);
      overlay.appendChild(sheet);
      return overlay;
    }

    if (availability.error) {
      const err = document.createElement("div");
      err.className = "gtd-availability-status";
      err.dataset.state = "error";
      err.textContent = `Cannot read calendar: ${availability.error}`;
      sheet.appendChild(err);
      overlay.appendChild(sheet);
      return overlay;
    }

    const daySlots = buildDaySlots(availability);
    if (daySlots.length === 0) {
      const none = document.createElement("div");
      none.className = "gtd-muted";
      none.textContent = "No slots in the next 2 weeks.";
      sheet.appendChild(none);
      overlay.appendChild(sheet);
      return overlay;
    }

    if (!state.booking.selectedAvailabilityDate) {
      state.booking.selectedAvailabilityDate = daySlots[0]!.key;
    }

    const dayPicker = document.createElement("div");
    dayPicker.className = "gtd-day-picker";
    daySlots.forEach((day) => {
      const btn = document.createElement("button");
      btn.className = "gtd-day";
      if (state.booking.selectedAvailabilityDate === day.key) {
        btn.dataset.selected = "true";
      }
      btn.textContent = day.label;
      btn.onclick = () => {
        state.booking.selectedAvailabilityDate = day.key;
        state.booking.status = undefined;
        render();
      };
      dayPicker.appendChild(btn);
    });
    sheet.appendChild(dayPicker);

    const selected =
      daySlots.find((d) => d.key === state.booking.selectedAvailabilityDate) || daySlots[0];
    const grid = document.createElement("div");
    grid.className = "gtd-slot-grid";
    if (!selected || selected.slots.length === 0) {
      const none = document.createElement("div");
      none.className = "gtd-muted";
      none.textContent = "No times available for this day.";
      grid.appendChild(none);
    } else {
      selected.slots.forEach((slot) => {
        const btn = document.createElement("button");
        btn.className = "gtd-slot";
        btn.dataset.state = slot.status;
        btn.textContent = slot.label;
        btn.onclick = () => {
          if (slot.status !== "free") {
            state.booking.status = { type: "error", text: "That time is already booked." };
            render();
            return;
          }
          state.booking.desiredAt = slot.iso;
          state.booking.availabilitySheetOpen = false;
          state.booking.status = undefined;
          render();
        };
        grid.appendChild(btn);
      });
    }
    sheet.appendChild(grid);

    if (availability.businessHours) {
      sheet.appendChild(buildHoursList(availability.businessHours));
    }

    overlay.appendChild(sheet);
    return overlay;
  }

  type SlotOption = { iso: string; label: string; dayLabel: string };

  type DaySlots = {
    key: string;
    label: string;
    slots: { iso: string; label: string; status: SlotStatus }[];
  };

  function buildDaySlots(availability: AvailabilityState): DaySlots[] {
    const days: DaySlots[] = [];
    const durationMinutes = DEFAULT_APPOINTMENT_MINUTES;
    const tz =
      availability.timeZone ||
      availability.businessHours?.timeZone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;
    const busy = availability.busy ?? [];
    const hours = availability.businessHours;
    const startWindow = availability.windowStart ? new Date(availability.windowStart) : new Date();
    for (let i = 0; i < 14; i++) {
      const day = new Date(startWindow);
      day.setHours(0, 0, 0, 0);
      day.setDate(day.getDate() + i);
      const ranges = getRangesForDay(day, hours) ?? [];
      const slots: { iso: string; label: string; status: SlotStatus }[] = [];
      if (ranges.length === 0) {
        days.push({
          key: day.toDateString(),
          label: dayLabel(day),
          slots: [{ iso: "", label: "Closed", status: "closed" }]
        });
        continue;
      }
      ranges.forEach((range) => {
        const startDate = setTimeOnDate(day, range.start);
        const endOfRange = setTimeOnDate(day, range.end);
        let cursor = new Date(startDate);
        while (cursor.getTime() + durationMinutes * 60 * 1000 <= endOfRange.getTime()) {
          const end = new Date(cursor.getTime() + durationMinutes * 60 * 1000);
          const conflict = busy.some((b) => slotOverlaps(b, cursor, end));
          slots.push({
            iso: cursor.toISOString(),
            label: formatRange(cursor, end, tz),
            status: conflict ? "busy" : "free"
          });
          cursor = new Date(cursor.getTime() + durationMinutes * 60 * 1000);
        }
      });
      days.push({
        key: day.toDateString(),
        label: dayLabel(day),
        slots
      });
    }
    return days;
  }

  function getRangesForDay(day: Date, hours?: BusinessHours) {
    if (!hours || !hours.weekly) {
      return [{ start: "09:00", end: "17:00" }];
    }
    const dayName = new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(day).toLowerCase();
    const entry = hours.weekly.find((w) => w.day === dayName);
    if (!entry || !entry.ranges || entry.ranges.length === 0) return [];
    return entry.ranges;
  }

  function setTimeOnDate(day: Date, hhmm: string) {
    const [hStr, mStr] = hhmm.split(":");
    const h = Number.parseInt(hStr ?? "0", 10);
    const m = Number.parseInt(mStr ?? "0", 10);
    const next = new Date(day);
    next.setHours(h, m, 0, 0);
    return next;
  }

  function buildBusyList(busy: BusyWindow[], timeZone?: string) {
    const wrapper = document.createElement("div");
    wrapper.className = "gtd-busy-list";
    if (!busy || busy.length === 0) {
      const empty = document.createElement("div");
      empty.className = "gtd-muted";
      empty.textContent = "No conflicts found.";
      wrapper.appendChild(empty);
      return wrapper;
    }

    const list = document.createElement("ul");
    list.className = "gtd-availability-list";
    busy.slice(0, 6).forEach((window) => {
      const li = document.createElement("li");
      li.textContent = formatWindow(window, timeZone);
      list.appendChild(li);
    });
    wrapper.appendChild(list);
    if (busy.length > 6) {
      const more = document.createElement("div");
      more.className = "gtd-muted";
      more.textContent = `+${busy.length - 6} more busy blocks`;
      wrapper.appendChild(more);
    }
    return wrapper;
  }

  function buildHoursList(hours: BusinessHours) {
    const wrapper = document.createElement("div");
    wrapper.className = "gtd-hours";
    const title = document.createElement("div");
    title.className = "gtd-hours-title";
    title.textContent = `Company hours${hours.timeZone ? ` (${hours.timeZone})` : ""}`;
    wrapper.appendChild(title);

    const list = document.createElement("div");
    list.className = "gtd-hours-list";
    const order: DayOfWeek[] = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday"
    ];
    order.forEach((day) => {
      const line = document.createElement("div");
      line.className = "gtd-hours-row";
      const dayLabel = document.createElement("span");
      dayLabel.className = "gtd-hours-day";
      dayLabel.textContent = day.slice(0, 3).toUpperCase();
      const rangesLabel = document.createElement("span");
      rangesLabel.className = "gtd-hours-range";
      const entry = hours.weekly.find((w) => w.day === day);
      rangesLabel.textContent =
        entry && entry.ranges.length > 0
          ? entry.ranges.map((r) => `${r.start}–${r.end}`).join(", ")
          : "Closed";
      line.appendChild(dayLabel);
      line.appendChild(rangesLabel);
      list.appendChild(line);
    });
    wrapper.appendChild(list);
    return wrapper;
  }

  function getSlotIssue(): SlotIssue | null {
    const desiredAtRaw = state.booking.desiredAt.trim();
    if (!desiredAtRaw) {
      return { type: "missing", message: "Pick a time to check availability." };
    }
    const start = toDate(desiredAtRaw);
    if (!start) {
      return { type: "invalid", message: "Enter a valid date and time." };
    }
    const end = new Date(start.getTime() + DEFAULT_APPOINTMENT_MINUTES * 60 * 1000);
    const availability = state.booking.availability;
    if (!availability.loading && availability.businessHours) {
      if (isOutsideBusinessHours(start, end, availability.businessHours)) {
        return { type: "closed", message: "That time is outside company hours." };
      }
    }
    if (!availability.loading) {
      const conflict = availability.busy.find((window) => slotOverlaps(window, start, end));
      if (conflict) {
        return {
          type: "busy",
          message: `Already booked ${formatWindow(conflict, availability.timeZone)}`
        };
      }
    }
    return null;
  }

  function slotOverlaps(window: BusyWindow, start: Date, end: Date) {
    const windowStart = toDate(window.start);
    const windowEnd = toDate(window.end);
    if (!windowStart || !windowEnd) return false;
    return start < windowEnd && windowStart < end;
  }

  function isOutsideBusinessHours(start: Date, end: Date, businessHours: BusinessHours) {
    if (!businessHours.weekly || businessHours.weekly.length === 0) return false;
    const timeZone = businessHours.timeZone || "UTC";
    const map = buildWeeklyMap(businessHours.weekly);
    const startLocal = toLocalDayMinutes(start, timeZone);
    const endLocal = toLocalDayMinutes(end, timeZone);
    if (!startLocal || !endLocal) return false;
    if (startLocal.day === endLocal.day) {
      return !rangeWithinHours(startLocal.day, startLocal.minutes, endLocal.minutes, map);
    }
    return (
      !rangeWithinHours(startLocal.day, startLocal.minutes, startLocal.minutes, map) ||
      !rangeWithinHours(endLocal.day, 0, endLocal.minutes, map)
    );
  }

  function buildWeeklyMap(weekly: { day: DayOfWeek; ranges: TimeRange[] }[]) {
    const map: Record<number, { start: number; end: number }[]> = {};
    weekly.forEach((entry) => {
      const dayIndex = weekdayToIndex(entry.day);
      if (dayIndex === null) return;
      const ranges = entry.ranges
        .map((range) => ({
          start: parseTimeToMinutes(range.start),
          end: parseTimeToMinutes(range.end)
        }))
        .filter(
          (range): range is { start: number; end: number } =>
            range.start !== null && range.end !== null && range.start < range.end
        )
        .sort((a, b) => a.start - b.start);
      if (ranges.length > 0) {
        map[dayIndex] = ranges;
      }
    });
    return map;
  }

  function weekdayToIndex(day?: string) {
    if (!day) return null;
    switch (day.toLowerCase()) {
      case "sunday":
        return 0;
      case "monday":
        return 1;
      case "tuesday":
        return 2;
      case "wednesday":
        return 3;
      case "thursday":
        return 4;
      case "friday":
        return 5;
      case "saturday":
        return 6;
      default:
        return null;
    }
  }

  function parseTimeToMinutes(value?: string) {
    if (!value || !/^\d{2}:\d{2}$/.test(value)) return null;
    const [hour, minute] = value.split(":").map((part) => Number.parseInt(part, 10));
    if (hour === undefined || minute === undefined || Number.isNaN(hour) || Number.isNaN(minute))
      return null;
    return hour * 60 + minute;
  }

  function toLocalDayMinutes(date: Date, timeZone: string) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        weekday: "long",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      }).formatToParts(date);
      const dayName = parts.find((p) => p.type === "weekday")?.value;
      const hourStr = parts.find((p) => p.type === "hour")?.value;
      const minuteStr = parts.find((p) => p.type === "minute")?.value;
      if (!dayName || !hourStr || !minuteStr) return null;
      const day = weekdayToIndex(dayName);
      if (day === null) return null;
      const hour = Number.parseInt(hourStr, 10);
      const minute = Number.parseInt(minuteStr, 10);
      if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
      return { day, minutes: hour * 60 + minute };
    } catch {
      return null;
    }
  }

  function rangeWithinHours(
    day: number,
    startMinutes: number,
    endMinutes: number,
    map: Record<number, { start: number; end: number }[]>
  ) {
    const ranges = map[day];
    if (!ranges || ranges.length === 0) return false;
    return ranges.some((range) => startMinutes >= range.start && endMinutes <= range.end);
  }

  function formatWindow(window: BusyWindow, timeZone?: string) {
    const start = toDate(window.start);
    const end = toDate(window.end);
    if (!start || !end) return "Busy";
    return formatRange(start, end, timeZone);
  }

  function formatRange(start: Date, end: Date, timeZone?: string) {
    const fmt = new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone
    });
    const startLabel = fmt.format(start);
    const endLabel = new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
      timeZone
    }).format(end);
    return `${startLabel} - ${endLabel}${timeZone ? ` (${timeZone})` : ""}`;
  }

  function toDate(value?: string) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date;
  }

  function dayLabel(day: Date) {
    const fmt = new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric"
    });
    return fmt.format(day);
  }

  function buildGreeting() {
    const title = document.title?.trim();
    const host = window.location.hostname.replace(/^www\./, "");
    const business = title && title.length <= 60 ? title : host || "this business";
    return `Hi, welcome to ${business}! I’m the live AI assistant—how can I help today?`;
  }

  function buildField(
    label: string,
    placeholder: string,
    value: string,
    onChange: (val: string) => void,
    multiline = false
  ) {
    const wrapper = document.createElement("label");
    wrapper.className = "gtd-field";
    const span = document.createElement("span");
    span.textContent = label;
    wrapper.appendChild(span);
    if (multiline) {
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.placeholder = placeholder;
      textarea.oninput = (e) => onChange((e.target as HTMLTextAreaElement).value);
      wrapper.appendChild(textarea);
    } else {
      const input = document.createElement("input");
      input.type = "text";
      input.value = value;
      input.placeholder = placeholder;
      input.oninput = (e) => onChange((e.target as HTMLInputElement).value);
      wrapper.appendChild(input);
    }
    return wrapper;
  }

  async function sendAction() {
    const name = state.contact.name.trim() || "Website visitor";
    const email = state.contact.email.trim();
    const notes = state.contact.notes.trim();
    const contact = email || name || "visitor";
    if (!contact || contact.length < 3) {
      state.actionStatus = { type: "error", text: "Add at least a name or email." };
      render();
      return;
    }
    state.actionLoading = true;
    state.actionStatus = undefined;
    render();
    try {
      const actionPath = state.contactMode === "call" ? "call" : "message";
      const resp = await fetch(`${API_BASE}/v1/actions/${actionPath}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          siteKey,
          sessionId,
          customerName: name,
          contact,
          email: email || undefined,
          notes: [notes, `Chat context:\n${chatContextSnippet()}`].filter(Boolean).join("\n\n"),
          pageUrl: window.location.href
        })
      });
      if (!resp.ok) {
        throw new Error(`Action API error: ${resp.status}`);
      }
      state.actionStatus = { type: "success", text: "Sent. The team will reach out soon." };
      state.contact = { name: "", email: "", notes: "" };
      state.contactMode = "message";
      state.suggestion = undefined;
    } catch (err) {
      console.error("[gtd-widget] action error", err);
      state.actionStatus = { type: "error", text: "Could not send. Please try again." };
    } finally {
      state.actionLoading = false;
      render();
    }
  }

  function maybeOpenBooking(text: string) {
    const intentWords = ["book", "booking", "schedule", "appointment", "meeting"];
    const lower = text.toLowerCase();
    if (intentWords.some((w) => lower.includes(w))) {
      openBooking(true);
    }
  }

  function applySuggestion(suggestion: ActionSuggestion) {
    state.suggestion = undefined;
    if (suggestion.type === "book") {
      if (suggestion.customerName) state.booking.name = suggestion.customerName;
      if (suggestion.email) state.booking.email = suggestion.email;
      if (suggestion.desiredAt) state.booking.desiredAt = suggestion.desiredAt;
      if (suggestion.notes) state.booking.notes = suggestion.notes;
      openBooking(true);
      return;
    } else if (suggestion.type === "call") {
      state.contactOpen = true;
      state.contactMode = "call";
      if (suggestion.customerName) state.contact.name = suggestion.customerName;
      if (suggestion.email) state.contact.email = suggestion.email;
      if (suggestion.notes) state.contact.notes = suggestion.notes;
    } else if (suggestion.type === "message") {
      state.contactOpen = true;
      state.contactMode = "message";
      if (suggestion.customerName) state.contact.name = suggestion.customerName;
      if (suggestion.email) state.contact.email = suggestion.email;
      if (suggestion.notes) state.contact.notes = suggestion.notes;
    }
    render();
  }

  function chatContextSnippet(limit = 6) {
    const recent = state.messages.slice(-limit);
    return recent.map((m) => `${m.role}: ${m.content}`).join("\n");
  }

  async function sendBooking() {
    const name = state.booking.name.trim() || "Website visitor";
    const email = state.booking.email.trim();
    const desiredAtRaw = state.booking.desiredAt.trim();
    const notesInput = state.booking.notes.trim();
    const contact = email || name || "visitor";

    const slotIssue = getSlotIssue();
    if (slotIssue) {
      state.booking.status = { type: "error", text: slotIssue.message };
      render();
      return;
    }

    state.booking.loading = true;
    state.booking.status = undefined;
    render();

    let desiredAt: string | undefined = undefined;
    if (desiredAtRaw) {
      const parsed = new Date(desiredAtRaw);
      desiredAt = Number.isNaN(parsed.getTime()) ? desiredAtRaw : parsed.toISOString();
    }

    const combinedNotes = [notesInput, `Chat context:\n${chatContextSnippet()}`]
      .filter(Boolean)
      .join("\n\n");

    try {
      const resp = await fetch(`${API_BASE}/v1/actions/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          siteKey,
          sessionId,
          customerName: name,
          contact,
          email: email || undefined,
          desiredAt: desiredAt || undefined,
          notes: combinedNotes || undefined,
          pageUrl: window.location.href
        })
      });
      if (!resp.ok) {
        throw new Error(`Action API error: ${resp.status}`);
      }
      const data = (await resp.json()) as { calendar?: CalendarResult };
      if (data.calendar && data.calendar.status !== "scheduled") {
        const reason = data.calendar.reason || data.calendar.error || "Not available.";
        state.booking.status = { type: "error", text: `Could not book: ${reason}` };
      } else {
        state.booking.status = {
          type: "success",
          text: "Booked. You’ll see a confirmation soon."
        };
        state.booking.open = false;
        state.booking.name = "";
        state.booking.email = "";
        state.booking.desiredAt = "";
        state.booking.notes = "";
      }
    } catch (err) {
      console.error("[gtd-widget] booking error", err);
      state.booking.status = { type: "error", text: "Could not book. Please try again." };
    } finally {
      state.booking.loading = false;
      render();
    }
  }
}

function attachStyles(shadow: ShadowRoot, vars: Record<string, string>) {
  const styleEl = document.createElement("style");
  const cssVars = Object.entries(vars)
    .map(([k, v]) => `${k}: ${v};`)
    .join("");
  styleEl.textContent = `:host { ${cssVars} } ${styleText}`;
  shadow.appendChild(styleEl);
}

function ensureSession(siteKey: string): string {
  const key = `gtd_widget_session_${siteKey}`;
  // Prefer tab-scoped sessionStorage to avoid sharing transcripts across different visitors on the same device.
  const fromSession = sessionStorage.getItem(key);
  if (fromSession) return fromSession;

  // Cleanup any old localStorage value to avoid reuse.
  localStorage.removeItem(key);

  const next = createSessionId();
  sessionStorage.setItem(key, next);
  return next;
}
